import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Gallery } from '../../types/Gallery';
import sitePaths from '../../utils/sitePaths';
import categoryContent from '../../utils/categoryContent';
import './CategoryManagement.css';
import './ui-enhancements.css';

interface CategoryFaq {
  question: string;
  answer: string;
}

interface Category {
  id: string;
  name: string;
  slug: string;
  description: string;
  color: string;
  createdAt: string;
  updatedAt?: string;
  order?: number;
  previousSlugs?: string[];
  seoTitle?: string;
  seoDescription?: string;
  h1?: string;
  intro?: string;
  body?: string;
  faqs?: CategoryFaq[];
}

const EMPTY_FORM = {
  name: '',
  slug: '',
  description: '',
  color: '#8B4513',
  seoTitle: '',
  seoDescription: '',
  h1: '',
  intro: '',
  body: '',
  faqs: [] as CategoryFaq[],
};

interface CategoryManagementProps {
  galleries: Gallery[];
}

const CategoryManagement: React.FC<CategoryManagementProps> = ({ galleries }) => {
  // Helper function to get CSS variable value
  const getCSSVariable = (variableName: string, fallback: string = '#8B4513'): string => {
    if (typeof window === 'undefined') return fallback;
    const root = document.documentElement;
    const value = getComputedStyle(root).getPropertyValue(variableName).trim();
    return value || fallback;
  };

  const getDefaultColor = (): string => {
    return getCSSVariable('--color-primary', '#8B4513');
  };

  const [categories, setCategories] = useState<Category[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [isSortMode, setIsSortMode] = useState(false);
  const [draggedCategoryIndex, setDraggedCategoryIndex] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    ...EMPTY_FORM,
    color: getDefaultColor(),
  });

  useEffect(() => {
    loadCategories();
  }, []);

  // Update default color when theme changes
  useEffect(() => {
    if (!formData.color || formData.color === '#8B4513') {
      setFormData(prev => ({ ...prev, color: getDefaultColor() }));
    }
  }, []);

  const loadCategories = async () => {
    try {
      // Try server first
      const { loadLiveCategories } = await import('../../utils/CategoryAPI');
      const serverCategories = await loadLiveCategories();
      
      // Always use server categories (even if empty) - no localStorage fallback
      if (serverCategories.length > 0) {
        // Ensure categories have order field
        const categoriesWithOrder = serverCategories.map((category, index) => ({
          ...category,
          order: category.order || (index + 1)
        }));
        
        // Sort by order
        const sortedCategories = categoriesWithOrder.sort((a, b) => (a.order || 0) - (b.order || 0));
        
        setCategories(sortedCategories);
      } else {
        // Server returned empty categories - use empty array (no localStorage fallback)
        setCategories([]);
      }
      
    } catch (error) {
      console.error('Error loading categories:', error);
      // No localStorage fallback - use empty array
      setCategories([]);
    }
  };

  const saveCategories = async (categoriesToSave: Category[]) => {
    try {
      // Ensure all categories have order field
      const categoriesWithOrder = categoriesToSave.map((category, index) => ({
        ...category,
        order: category.order || (index + 1)
      }));
      
      // Save to server (truly live for all visitors)
      const { saveCategoriesToServer, renameCategoryOnServer } = await import('../../utils/CategoryAPI');

      // Detect slug renames and apply global rename (updates galleries)
      const previous = categories;
      const slugChanges: { oldSlug: string; newSlug: string; newName?: string; newColor?: string }[] = [];
      categoriesWithOrder.forEach((cat) => {
        const before = previous.find(p => (p.id && p.id === cat.id) || (p.slug === cat.slug) || (p.name === cat.name));
        if (before && before.slug !== cat.slug) {
          slugChanges.push({ oldSlug: before.slug, newSlug: cat.slug, newName: cat.name, newColor: cat.color });
        }
      });

      for (const change of slugChanges) {
        try {
          await renameCategoryOnServer(change.oldSlug, change.newSlug, change.newName, change.newColor);
        } catch (e) {
          console.error('Failed to rename category globally:', change, e);
        }
      }

      await saveCategoriesToServer(categoriesWithOrder);
      
      // Also save to localStorage as backup
      localStorage.setItem('admin_categories', JSON.stringify(categoriesWithOrder));
      
      setCategories(categoriesWithOrder);
      
      // Dispatch custom event to notify other components
      window.dispatchEvent(new CustomEvent('categories_updated'));
      
      // Also dispatch a storage event to trigger cross-tab updates
      window.dispatchEvent(new StorageEvent('storage', {
        key: 'admin_categories',
        newValue: JSON.stringify(categoriesWithOrder),
        oldValue: localStorage.getItem('admin_categories'),
        storageArea: localStorage
      }));
    } catch (error) {
      console.error('Error saving categories:', error);
    }
  };

  // Drag and drop handlers
  const handleCategoryDragStart = (e: React.DragEvent, index: number) => {
    setDraggedCategoryIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleCategoryDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleCategoryDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    
    if (draggedCategoryIndex === null || draggedCategoryIndex === dropIndex) {
      setDraggedCategoryIndex(null);
      return;
    }

    const newCategories = [...categories];
    const draggedCategory = newCategories[draggedCategoryIndex];
    
    // Remove dragged category
    newCategories.splice(draggedCategoryIndex, 1);
    
    // Insert at new position
    newCategories.splice(dropIndex, 0, draggedCategory);
    
    // Update order values
    const reorderedCategories = newCategories.map((category, index) => ({
      ...category,
      order: index + 1
    }));
    
    setCategories(reorderedCategories);
    setDraggedCategoryIndex(null);
    
    // Save the new order
    saveCategories(reorderedCategories);
  };

  const handleCategoryDragEnd = () => {
    setDraggedCategoryIndex(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.slug.trim()) {
      alert('Name and slug are required');
      return;
    }

    const newCategory: Category = {
      ...(editingCategory || {}),
      id: editingCategory ? editingCategory.id : Date.now().toString(),
      name: formData.name.trim(),
      slug: formData.slug.trim(),
      description: formData.description.trim(),
      color: formData.color,
      seoTitle: formData.seoTitle.trim(),
      seoDescription: formData.seoDescription.trim(),
      h1: formData.h1.trim(),
      intro: formData.intro.trim(),
      body: formData.body.trim(),
      faqs: formData.faqs
        .map((item) => ({ question: item.question.trim(), answer: item.answer.trim() }))
        .filter((item) => item.question || item.answer),
      createdAt: editingCategory ? editingCategory.createdAt : new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    let updatedCategories: Category[];
    
    if (editingCategory) {
      updatedCategories = categories.map(cat => 
        cat.id === editingCategory.id ? newCategory : cat
      );
    } else {
      updatedCategories = [...categories, newCategory];
    }

    saveCategories(updatedCategories);
    resetForm();
    setShowModal(false);
  };

  const resetForm = () => {
    setFormData({ ...EMPTY_FORM, color: getDefaultColor() });
    setEditingCategory(null);
  };

  useEffect(() => {
    if (!showModal) return undefined;

    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowModal(false);
        resetForm();
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleEscKey);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [showModal]);

  const contentDefaults = (categoryContent.DEFAULT_CATEGORY_CONTENT as Record<string, {
    seoTitle?: string;
    seoDescription?: string;
    h1?: string;
    intro?: string;
    body?: string;
    faqs?: CategoryFaq[];
  }>)[sitePaths.slugify(formData.slug)] || {};

  const updateFaq = (index: number, patch: Partial<CategoryFaq>) => {
    setFormData((current) => ({
      ...current,
      faqs: current.faqs.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item)),
    }));
  };

  const moveFaq = (index: number, direction: -1 | 1) => {
    setFormData((current) => {
      const next = current.faqs.slice();
      const target = index + direction;
      if (target < 0 || target >= next.length) return current;
      const [row] = next.splice(index, 1);
      next.splice(target, 0, row);
      return { ...current, faqs: next };
    });
  };

  const editCategory = (category: Category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      slug: category.slug,
      description: category.description || '',
      color: category.color,
      seoTitle: category.seoTitle || '',
      seoDescription: category.seoDescription || '',
      h1: category.h1 || '',
      intro: category.intro || '',
      body: category.body || '',
      faqs: Array.isArray(category.faqs) ? category.faqs.map((item) => ({
        question: item.question || '',
        answer: item.answer || '',
      })) : [],
    });
    setShowModal(true);
  };

  const deleteCategory = (categoryId: string) => {
    const category = categories.find(cat => cat.id === categoryId);
    if (!category) return;

    const galleriesUsingCategory = galleries.filter(gallery => sitePaths.galleryCategorySlugs(gallery).includes(sitePaths.slugify(category.slug)));
    
    if (galleriesUsingCategory.length > 0) {
      alert(`Cannot delete category "${category.name}" because it is used by ${galleriesUsingCategory.length} galleries. Please reassign or delete those galleries first.`);
      return;
    }

    if (window.confirm(`Are you sure you want to delete the category "${category.name}"?`)) {
      const updatedCategories = categories.filter(cat => cat.id !== categoryId);
      saveCategories(updatedCategories);
    }
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9 -]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleNameChange = (name: string) => {
    setFormData({
      ...formData,
      name,
      slug: generateSlug(name),
    });
  };

  return (
    <section className="admin-section">
      <div className="section-header">
        <h2>Gallery categories</h2>
        <div className="section-controls">
          {categories.length > 1 && (
            <button 
              className={`btn ${isSortMode ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setIsSortMode(!isSortMode)}
            >
              <i className="fas fa-sort"></i> {isSortMode ? 'Exit Sort' : 'Sort Order'}
            </button>
          )}
          <button 
            className="btn btn-primary" 
            onClick={() => setShowModal(true)}
          >
            <i className="fas fa-plus"></i> Add New Category
          </button>
        </div>
      </div>

      <div className="categories-list">
        {categories.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-tags"></i>
            <h3>No Categories Yet</h3>
            <p>Create your first gallery category to organize your work.</p>
            <button className="btn btn-primary" onClick={() => setShowModal(true)}>
              <i className="fas fa-plus"></i> Create Category
            </button>
          </div>
        ) : (
          <div className={`categories-container ${isSortMode ? 'sort-mode' : ''}`}>
            {categories.map((category, index) => {
              const galleryCount = galleries.filter(g => sitePaths.galleryCategorySlugs(g).includes(sitePaths.slugify(category.slug))).length;
              
              return (
                <div 
                  key={category.id} 
                  className={`category-item ${draggedCategoryIndex === index ? 'dragging' : ''}`}
                  draggable={isSortMode}
                  onDragStart={(e) => handleCategoryDragStart(e, index)}
                  onDragOver={handleCategoryDragOver}
                  onDrop={(e) => handleCategoryDrop(e, index)}
                  onDragEnd={handleCategoryDragEnd}
                  style={{ cursor: isSortMode ? 'move' : 'default' }}
                >
                  {isSortMode && (
                    <div className="drag-handle">
                      <i className="fas fa-grip-vertical"></i>
                    </div>
                  )}
                  
                  <div className="category-info">
                    <div 
                      className="category-color" 
                      style={{ backgroundColor: category.color }}
                    ></div>
                    <div className="category-details">
                      <h3 className="category-name">{category.name}</h3>
                      <p className="category-description">{category.description || 'No description'}</p>
                      <div className="category-meta">
                        <span className="gallery-count">{galleryCount} galleries</span>
                        <span className="category-slug">{category.slug}</span>
                        {isSortMode && (
                          <span className="sort-order">Order: {category.order || index + 1}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {!isSortMode && (
                    <div className="category-actions">
                      <button 
                        className="btn btn-primary btn-sm"
                        aria-label={`Edit ${category.name}`}
                        onClick={() => editCategory(category)}
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button 
                        className="btn btn-danger btn-sm"
                        onClick={() => deleteCategory(category.id)}
                        disabled={galleryCount > 0}
                        title={galleryCount > 0 ? 'Cannot delete: Category has galleries' : ''}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showModal && createPortal(
        <div
          className="gallery-category-dialog-backdrop"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              setShowModal(false);
              resetForm();
            }
          }}
        >
          <div
            className="gallery-category-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="gallery-category-dialog-title"
          >
            <div className="gallery-category-dialog-header">
              <h3 id="gallery-category-dialog-title">{editingCategory ? 'Edit category' : 'Add category'}</h3>
              <button
                type="button"
                className="gallery-category-dialog-close"
                aria-label="Close"
                onClick={() => { setShowModal(false); resetForm(); }}
              >
                &times;
              </button>
            </div>
            <form className="gallery-category-dialog-form" onSubmit={handleSubmit}>
              <div className="gallery-category-dialog-fields">
                <label className="gallery-category-field" htmlFor="category-name">
                  Category name
                  <input
                    type="text"
                    id="category-name"
                    value={formData.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    placeholder="Enter category name"
                    autoFocus
                    required
                  />
                </label>
                <label className="gallery-category-field" htmlFor="category-slug">
                  Web address
                  <input
                    type="text"
                    id="category-slug"
                    value={formData.slug}
                    onChange={(e) => setFormData({...formData, slug: e.target.value})}
                    placeholder="category-slug"
                    required
                  />
                </label>
                <label className="gallery-category-field" htmlFor="category-description">
                  Description
                  <textarea
                    id="category-description"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    rows={5}
                    placeholder="Category description. Press Enter to start a new line."
                  />
                  <small>Line breaks are kept and shown in the gallery information window.</small>
                </label>
                <label className="gallery-category-field" htmlFor="category-seo-title">
                  SEO title
                  <input
                    type="text"
                    id="category-seo-title"
                    value={formData.seoTitle}
                    onChange={(e) => setFormData({ ...formData, seoTitle: e.target.value })}
                    placeholder={contentDefaults.seoTitle || 'Shown as the page title in search'}
                  />
                  <small className={formData.seoTitle.length > 60 ? 'category-count-warn' : ''}>
                    {formData.seoTitle.length} characters. Search titles work best at 60 or fewer.
                  </small>
                </label>
                <label className="gallery-category-field" htmlFor="category-seo-description">
                  Meta description
                  <textarea
                    id="category-seo-description"
                    value={formData.seoDescription}
                    onChange={(e) => setFormData({ ...formData, seoDescription: e.target.value })}
                    rows={3}
                    placeholder={contentDefaults.seoDescription || 'Short summary shown under the title in search'}
                  />
                  <small className={formData.seoDescription.length > 155 ? 'category-count-warn' : ''}>
                    {formData.seoDescription.length} characters. Keep this at 155 or fewer.
                  </small>
                </label>
                <label className="gallery-category-field" htmlFor="category-h1">
                  Page heading (H1)
                  <input
                    type="text"
                    id="category-h1"
                    value={formData.h1}
                    onChange={(e) => setFormData({ ...formData, h1: e.target.value })}
                    placeholder={contentDefaults.h1 || formData.name || 'Heading visitors see'}
                  />
                </label>
                <label className="gallery-category-field" htmlFor="category-intro">
                  Intro
                  <textarea
                    id="category-intro"
                    value={formData.intro}
                    onChange={(e) => setFormData({ ...formData, intro: e.target.value })}
                    rows={6}
                    placeholder={contentDefaults.intro || 'Opening paragraphs. A blank line starts a new paragraph.'}
                  />
                </label>
                <label className="gallery-category-field" htmlFor="category-body">
                  Page text
                  <textarea
                    id="category-body"
                    value={formData.body}
                    onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                    rows={10}
                    placeholder={contentDefaults.body || 'The rest of the page.'}
                  />
                  <small>Use ## and ### for headings, a blank line between paragraphs, - for a bullet, **bold**, and [text](/path) for a link.</small>
                </label>
                <div className="gallery-category-field">
                  <span>Questions and answers</span>
                  {formData.faqs.map((item, index) => (
                    <div className="category-faq-row" key={`faq-${index}`}>
                      <input
                        type="text"
                        value={item.question}
                        aria-label={`Question ${index + 1}`}
                        placeholder="Question"
                        onChange={(e) => updateFaq(index, { question: e.target.value })}
                      />
                      <textarea
                        value={item.answer}
                        aria-label={`Answer ${index + 1}`}
                        rows={3}
                        placeholder="Answer"
                        onChange={(e) => updateFaq(index, { answer: e.target.value })}
                      />
                      <div className="category-faq-actions">
                        <button type="button" onClick={() => moveFaq(index, -1)} disabled={index === 0}>Up</button>
                        <button type="button" onClick={() => moveFaq(index, 1)} disabled={index === formData.faqs.length - 1}>Down</button>
                        <button type="button" onClick={() => setFormData({ ...formData, faqs: formData.faqs.filter((_, itemIndex) => itemIndex !== index) })}>Remove</button>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, faqs: formData.faqs.concat({ question: '', answer: '' }) })}
                  >
                    Add question
                  </button>
                  {!formData.faqs.length && contentDefaults.faqs ? (
                    <small>Leave this empty to keep the {contentDefaults.faqs.length} questions already written for this page.</small>
                  ) : null}
                </div>
                <div className="gallery-category-field">
                  <label htmlFor="category-color">Category colour</label>
                  <div className="color-picker-container">
                    <input
                      type="color"
                      id="category-color"
                      value={formData.color}
                      onChange={(e) => setFormData({...formData, color: e.target.value})}
                    />
                    <div className="color-presets">
                      <button type="button" className="color-preset" aria-label="Brown" style={{background: '#8B4513'}} onClick={() => setFormData({...formData, color: '#8B4513'})}></button>
                      <button type="button" className="color-preset" aria-label="Blue" style={{background: '#2c5aa0'}} onClick={() => setFormData({...formData, color: '#2c5aa0'})}></button>
                      <button type="button" className="color-preset" aria-label="Green" style={{background: '#2d5016'}} onClick={() => setFormData({...formData, color: '#2d5016'})}></button>
                      <button type="button" className="color-preset" aria-label="Tan" style={{background: '#a0522d'}} onClick={() => setFormData({...formData, color: '#a0522d'})}></button>
                      <button type="button" className="color-preset" aria-label="Purple" style={{background: '#6a5acd'}} onClick={() => setFormData({...formData, color: '#6a5acd'})}></button>
                      <button type="button" className="color-preset" aria-label="Red" style={{background: '#cd5c5c'}} onClick={() => setFormData({...formData, color: '#cd5c5c'})}></button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="gallery-category-dialog-actions">
                <button type="button" className="is-secondary" onClick={() => { setShowModal(false); resetForm(); }}>
                  Cancel
                </button>
                <button type="submit" className="is-primary">
                  {editingCategory ? 'Update category' : 'Save category'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </section>
  );
};

export default CategoryManagement;
